import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import ImagePicker from '@/components/ImagePicker';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, GraduationCap, ArrowUp, ArrowDown, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { resolveImageUrl } from '@/utils/imageUrl';
import { API_URL } from '@/config/api';

const AdminInstructors = () => {
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingInstructor, setEditingInstructor] = useState(null);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [formData, setFormData] = useState({ name: '', description: '', experience_years: 5, image_url: '', phone: '', email: '' });

  useEffect(() => {
    fetchInstructors();
    loadCategories();
  }, []);

  const fetchInstructors = async () => {
    try {
      const res = await axios.get(`${API_URL}/instructors`);
      setInstructors(res.data);
    } catch { toast.error('載入失敗'); }
    finally { setLoading(false); }
  };

  const loadCategories = async () => {
    try {
      const [vehicleRes, specialtyRes] = await Promise.all([
        axios.get(`${API_URL}/categories?type=course_type`),
        axios.get(`${API_URL}/categories?type=student_category`),
      ]);
      setVehicleTypes(vehicleRes.data.map(c => c.name));
      setSpecialtyOptions(specialtyRes.data.map(c => c.name));
    } catch {}
  };

  const toggleVehicle = (v) => setSelectedVehicles(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  const toggleSpecialty = (v) => setSelectedSpecialties(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedVehicles.length === 0) { toast.error('請選擇至少一種教授車種'); return; }
    try {
      const data = { ...formData, title: selectedVehicles.join(' / '), specialties: selectedSpecialties, email: formData.email || null };
      if (editingInstructor) {
        await axios.put(`${API_URL}/instructors/${editingInstructor.id}`, data);
        toast.success('更新成功');
      } else {
        await axios.post(`${API_URL}/instructors`, data);
        toast.success('新增成功');
      }
      setDialogOpen(false);
      resetForm();
      fetchInstructors();
    } catch { toast.error('操作失敗'); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_URL}/instructors/${deleteId}`);
      toast.success('刪除成功');
      fetchInstructors();
    } catch { toast.error('刪除失敗'); }
    finally { setDeleteId(null); }
  };

  const handleMove = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= instructors.length) return;
    const reordered = [...instructors];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setInstructors(reordered);
    try {
      await axios.post(`${API_URL}/instructors/reorder`, reordered.map((inst, i) => ({ id: inst.id, sort_order: i })));
      toast.success('次序已更新');
    } catch { toast.error('排序失敗'); fetchInstructors(); }
  };

  const openDialog = (instructor = null) => {
    if (instructor) {
      setEditingInstructor(instructor);
      const parts = instructor.title ? instructor.title.split(/\s*[/／]\s*/).map(v => v.trim()) : [];
      setSelectedVehicles(parts.filter(v => vehicleTypes.includes(v)));
      setSelectedSpecialties(instructor.specialties || []);
      setFormData({ name: instructor.name, description: instructor.description, experience_years: instructor.experience_years, image_url: instructor.image_url, phone: instructor.phone, email: instructor.email || '' });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingInstructor(null);
    setSelectedVehicles([]);
    setSelectedSpecialties([]);
    setFormData({ name: '', description: '', experience_years: 5, image_url: '', phone: '', email: '' });
  };

  const CheckboxGrid = ({ options, selected, onToggle, emptyMsg }) => (
    options.length === 0
      ? <p className="text-xs text-muted-foreground p-3 border rounded-lg bg-gray-50">{emptyMsg}</p>
      : (
        <div className="grid grid-cols-2 gap-2 p-3 border rounded-lg bg-gray-50">
          {options.map(v => (
            <label key={v} className="flex items-center gap-2 cursor-pointer text-sm select-none">
              <input type="checkbox" checked={selected.includes(v)} onChange={() => onToggle(v)} className="rounded" />
              {v}
            </label>
          ))}
        </div>
      )
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap justify-between items-start gap-3">
          <div>
            <h1 className="text-3xl font-bold">導師介紹管理</h1>
            <p className="text-muted-foreground">管理導師資料、簡介和照片</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => openDialog()} data-testid="add-instructor-button">
                <Plus className="mr-2 h-4 w-4" /> 新增導師
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingInstructor ? '編輯導師' : '新增導師'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>姓名 *</Label>
                  <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required className="mt-1" />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>教授車種 * <span className="text-xs text-muted-foreground font-normal">（可多選）</span></Label>
                    <a href="/admin/categories" target="_blank" rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                      管理課程類別 <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  <CheckboxGrid
                    options={vehicleTypes}
                    selected={selectedVehicles}
                    onToggle={toggleVehicle}
                    emptyMsg='尚無課程類別，請先到「類別管理 → 課程類別」新增'
                  />
                  {selectedVehicles.length > 0 && <p className="text-xs text-muted-foreground mt-1">已選：{selectedVehicles.join(' / ')}</p>}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>專長 <span className="text-xs text-muted-foreground font-normal">（可多選）</span></Label>
                    <a href="/admin/categories" target="_blank" rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                      管理學員類別 <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  <CheckboxGrid
                    options={specialtyOptions}
                    selected={selectedSpecialties}
                    onToggle={toggleSpecialty}
                    emptyMsg='尚無學員類別，請先到「類別管理 → 學員類別」新增'
                  />
                  {selectedSpecialties.length > 0 && <p className="text-xs text-muted-foreground mt-1">已選：{selectedSpecialties.join('、')}</p>}
                </div>

                <div>
                  <Label>簡介 *</Label>
                  <Textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} required rows={4} placeholder="每句以句號（。）分隔，前台會自動拆分為要點列表顯示" className="mt-1" />
                </div>
                <div>
                  <Label>經驗年資 *</Label>
                  <Input type="number" value={formData.experience_years} onChange={e => setFormData({ ...formData, experience_years: parseInt(e.target.value) })} required className="mt-1 w-32" />
                </div>
                <ImagePicker label="導師照片" value={formData.image_url} onChange={url => setFormData({ ...formData, image_url: url })} />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>電話</Label>
                    <Input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>電郵</Label>
                    <Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="mt-1" />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button type="submit">儲存</Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>取消</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Instructors Table */}
        {loading ? (
          <div className="text-center py-12">載入中...</div>
        ) : instructors.length > 0 ? (
          <div className="bg-white rounded-lg border overflow-x-auto">
            <table className="w-full min-w-[520px]" style={{ tableLayout: 'fixed' }}>
              <thead className="border-b bg-gray-50">
                <tr>
                  <th className="text-left p-3 overflow-hidden" style={{ width: '90px', resize: 'horizontal' }}>次序</th>
                  <th className="text-left p-3 overflow-hidden" style={{ width: '160px', resize: 'horizontal' }}>導師</th>
                  <th className="text-left p-3 overflow-hidden hidden md:table-cell" style={{ width: '220px', resize: 'horizontal' }}>教授車種</th>
                  <th className="text-left p-3 overflow-hidden hidden lg:table-cell" style={{ resize: 'horizontal' }}>簡介</th>
                  <th className="text-left p-3 overflow-hidden hidden md:table-cell" style={{ width: '70px', resize: 'horizontal' }}>年資</th>
                  <th className="text-right p-3 overflow-hidden" style={{ width: '100px' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {instructors.map((instructor, index) => {
                  const imgSrc = resolveImageUrl(instructor.image_url);
                  return (
                    <tr key={instructor.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="outline" onClick={() => handleMove(index, -1)} disabled={index === 0} title="上移" data-testid={`move-up-${instructor.id}`}><ArrowUp className="h-4 w-4" /></Button>
                          <Button size="sm" variant="outline" onClick={() => handleMove(index, 1)} disabled={index === instructors.length - 1} title="下移" data-testid={`move-down-${instructor.id}`}><ArrowDown className="h-4 w-4" /></Button>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {imgSrc ? (
                            <img src={imgSrc} alt={instructor.name} className="w-12 h-12 rounded-full object-cover border-2 border-blue-100 flex-shrink-0" />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0"><GraduationCap className="h-6 w-6 text-blue-400" /></div>
                          )}
                          <div>
                            <div className="font-semibold">{instructor.name}</div>
                            <div className="text-xs text-muted-foreground md:hidden">{instructor.title}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 hidden md:table-cell text-sm truncate" title={instructor.title}>{instructor.title}</td>
                      <td className="p-3 hidden lg:table-cell text-sm text-muted-foreground truncate" title={instructor.description}>{instructor.description || '—'}</td>
                      <td className="p-3 hidden md:table-cell text-sm">{instructor.experience_years} 年</td>
                      <td className="p-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="outline" onClick={() => openDialog(instructor)}><Pencil className="h-4 w-4" /></Button>
                          <Button size="sm" variant="destructive" onClick={() => setDeleteId(instructor.id)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">暫無導師資料，請點擊「新增導師」開始添加。</div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={o => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除此導師？</AlertDialogTitle>
            <AlertDialogDescription>此操作不可撤銷，導師資料將永久刪除。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminInstructors;
